import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RefreshCw, Save } from 'lucide-react'
import { homepageApi, mediaApi } from '../../services/api'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'
import RichTextEditor from '../../components/ui/RichTextEditor'
import { ADMIN_BASE_PATH } from '../../constants/adminRoutes'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'
import AdminImageField from '../../components/admin/AdminImageField'
import {
  getSectionConfig,
  getSectionDisplayName,
  getItemDisplayName,
  normalizeHeroStats,
  HOMEPAGE_FIELD_DEFS,
} from '../../constants/homepageAdminConfig'

function SectionNote({ noteKey }) {
  const { t } = useTranslation()
  if (!noteKey) return null
  const text = t(noteKey)
  if (!text || text === noteKey) return null
  return (
    <p className="rounded-lg border border-accent/30 bg-accent/5 px-4 py-3 text-sm text-text/70">
      {text.includes(`${ADMIN_BASE_PATH}/`) ? (
        <>
          {text.split(new RegExp(`(${ADMIN_BASE_PATH.replace('/', '\\/')}/[^\\s]+)`)).map((part, index) =>
            part.startsWith(`${ADMIN_BASE_PATH}/`) ? (
              <Link key={index} to={part} className="font-medium text-primary underline">
                {part}
              </Link>
            ) : (
              part
            ),
          )}
        </>
      ) : (
        text
      )}
    </p>
  )
}

function HeroStatsEditor({ form, setForm, t }) {
  const stats = normalizeHeroStats(form.settings?.stats)

  const updateStat = (index, field, value) => {
    const next = stats.map((stat, i) => (i === index ? { ...stat, [field]: value } : stat))
    setForm({ ...form, settings: { ...form.settings, stats: next } })
  }

  return (
    <div>
      <h4 className="mb-3 text-sm font-semibold text-primary">{t('admin.homepage.heroStatsTitle')}</h4>
      <div className="space-y-4">
        {stats.map((stat, index) => (
          <div key={stat.key} className="rounded-xl border border-border p-4">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-text/50">
              {t(`admin.homepage.statKeys.${stat.key}`)}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <AdminFormField label={t('admin.homepage.fields.statValue')} htmlFor={`stat-${stat.key}-value`}>
                <input
                  id={`stat-${stat.key}-value`}
                  value={stat.value}
                  onChange={(e) => updateStat(index, 'value', e.target.value)}
                  className={adminInputClass}
                />
              </AdminFormField>
              <AdminFormField label={t('admin.homepage.fields.labelEn')} htmlFor={`stat-${stat.key}-label-en`}>
                <input
                  id={`stat-${stat.key}-label-en`}
                  value={stat.label_en}
                  onChange={(e) => updateStat(index, 'label_en', e.target.value)}
                  className={adminInputClass}
                />
              </AdminFormField>
              <AdminFormField
                label={t('admin.homepage.fields.labelAr')}
                htmlFor={`stat-${stat.key}-label-ar`}
                className="sm:col-span-2"
              >
                <input
                  id={`stat-${stat.key}-label-ar`}
                  value={stat.label_ar}
                  onChange={(e) => updateStat(index, 'label_ar', e.target.value)}
                  dir="rtl"
                  lang="ar"
                  className={`${adminInputClass} font-ar`}
                />
              </AdminFormField>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ExcellencePanelEditor({ form, setForm, t }) {
  const panelEn = form.settings?.panel_en || ''
  const panelAr = form.settings?.panel_ar || ''

  return (
    <div>
      <h4 className="mb-3 text-sm font-semibold text-primary">{t('admin.homepage.excellencePanelTitle')}</h4>
      <div className="grid gap-3">
        <AdminFormField label={t('admin.homepage.fields.panelEn')} htmlFor="excellence-panel-en">
          <RichTextEditor
            value={panelEn}
            onChange={(html) => setForm({ ...form, settings: { ...form.settings, panel_en: html } })}
            minHeight="120px"
          />
        </AdminFormField>
        <AdminFormField label={t('admin.homepage.fields.panelAr')} htmlFor="excellence-panel-ar">
          <RichTextEditor
            value={panelAr}
            onChange={(html) => setForm({ ...form, settings: { ...form.settings, panel_ar: html } })}
            dir="rtl"
            minHeight="120px"
          />
        </AdminFormField>
      </div>
    </div>
  )
}

function FieldInput({ fieldKey, value, onChange, saving, sectionImageProps, t }) {
  const def = HOMEPAGE_FIELD_DEFS[fieldKey]
  if (!def) return null

  const label = t(def.labelKey)
  const isAr = fieldKey.endsWith('_ar')
  const inputId = `homepage-${fieldKey}`

  if (def.type === 'rich') {
    return (
      <AdminFormField key={fieldKey} label={label} htmlFor={inputId} className="md:col-span-2">
        <RichTextEditor
          value={value || ''}
          onChange={onChange}
          dir={isAr ? 'rtl' : 'ltr'}
          minHeight="140px"
        />
      </AdminFormField>
    )
  }

  if (def.type === 'image') {
    return (
      <AdminImageField
        key={fieldKey}
        label={label}
        htmlFor={inputId}
        className="md:col-span-2"
        value={value || ''}
        onChange={onChange}
        {...sectionImageProps}
        disabled={saving}
      />
    )
  }

  if (def.type === 'textarea') {
    return (
      <AdminFormField key={fieldKey} label={label} htmlFor={inputId} className="md:col-span-2">
        <textarea
          id={inputId}
          rows={3}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          dir={isAr ? 'rtl' : 'ltr'}
          lang={isAr ? 'ar' : 'en'}
          className={`${adminInputClass} ${isAr ? 'font-ar' : ''}`}
        />
      </AdminFormField>
    )
  }

  return (
    <AdminFormField
      key={fieldKey}
      label={label}
      htmlFor={inputId}
      className={fieldKey === 'link' || fieldKey.startsWith('button_link') ? 'md:col-span-2' : ''}
    >
      <input
        id={inputId}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        dir={isAr ? 'rtl' : 'ltr'}
        lang={isAr ? 'ar' : 'en'}
        className={`${adminInputClass} ${isAr ? 'font-ar' : ''}`}
      />
    </AdminFormField>
  )
}

function SectionEditor({ section, onSaved }) {
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'
  const config = getSectionConfig(section.key)
  const [form, setForm] = useState(section)
  const [items, setItems] = useState(section.items || [])
  const [sectionImageFile, setSectionImageFile] = useState(null)
  const [sectionImagePreview, setSectionImagePreview] = useState(null)
  const [saving, setSaving] = useState(false)
  const [uploadingItemIndex, setUploadingItemIndex] = useState(null)

  useEffect(() => {
    setForm(section.key === 'hero_stats' ? { ...section, settings: { ...section.settings, stats: normalizeHeroStats(section.settings?.stats) } } : section)
    setItems(section.items || [])
    if (sectionImagePreview?.startsWith('blob:')) URL.revokeObjectURL(sectionImagePreview)
    setSectionImageFile(null)
    setSectionImagePreview(null)
  }, [section])

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = {
        ...form,
        items,
        is_active: Boolean(form.is_active),
        ...(form.key === 'hero_stats'
          ? { settings: { ...form.settings, stats: normalizeHeroStats(form.settings?.stats) } }
          : {}),
      }
      if (sectionImageFile) {
        const fd = new FormData()
        Object.entries(payload).forEach(([key, value]) => {
          if (key === 'items') {
            fd.append('items', JSON.stringify(value))
            return
          }
          if (key === 'settings') {
            fd.append('settings', JSON.stringify(value))
            return
          }
          if (key === 'image') return
          if (value != null) {
            fd.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value))
          }
        })
        fd.append('image', sectionImageFile)
        await homepageApi.update(section.id, fd)
      } else {
        await homepageApi.update(section.id, payload)
      }
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const uploadItemImage = async (index, file) => {
    setUploadingItemIndex(index)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('folder', 'homepage')
      const { data } = await mediaApi.upload(fd)
      const uploaded = data.data || data
      updateItem(index, 'image', uploaded.path)
    } finally {
      setUploadingItemIndex(null)
    }
  }

  const updateItem = (index, key, value) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)))
  }

  const sectionImageProps = {
    imageFile: sectionImageFile,
    imagePreview: sectionImagePreview,
    onFileSelect: (file) => {
      if (sectionImagePreview?.startsWith('blob:')) URL.revokeObjectURL(sectionImagePreview)
      setSectionImageFile(file)
      setSectionImagePreview(URL.createObjectURL(file))
    },
  }

  const sectionFields = config.fields || []
  const itemFields = config.itemFields || []

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-primary">{getSectionDisplayName(section, t, isAr)}</h2>
            <p className="mt-1 text-xs text-text/50">{t(`admin.homepage.sectionTypes.${section.section_type}`, { defaultValue: section.section_type })}</p>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              form.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {form.is_active ? t('admin.homepage.active') : t('admin.homepage.inactive')}
          </span>
        </div>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto p-5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(form.is_active)}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          {t('admin.homepage.enableSection')}
        </label>

        <SectionNote noteKey={config.noteKey} />

        {sectionFields.length > 0 && (
          <div>
            <h4 className="mb-3 text-sm font-semibold text-primary">{t('admin.homepage.sectionContent')}</h4>
            <div className="grid gap-3 md:grid-cols-2">
              {sectionFields.map((fieldKey) => (
                <FieldInput
                  key={fieldKey}
                  fieldKey={fieldKey}
                  value={form[fieldKey]}
                  onChange={(value) => setForm({ ...form, [fieldKey]: value })}
                  saving={saving}
                  sectionImageProps={sectionImageProps}
                  t={t}
                />
              ))}
            </div>
          </div>
        )}

        {config.custom === 'heroStats' && <HeroStatsEditor form={form} setForm={setForm} t={t} />}
        {config.custom === 'excellencePanel' && <ExcellencePanelEditor form={form} setForm={setForm} t={t} />}

        {itemFields.length > 0 && items.length > 0 && (
          <div>
            <h4 className="mb-3 text-sm font-semibold text-primary">{t('admin.homepage.items')}</h4>
            <div className="space-y-3">
              {items.map((item, index) => (
                <div key={item.id || item.item_key || index} className="rounded-xl border border-border p-4">
                  <div className="mb-3 text-sm font-medium text-primary">{getItemDisplayName(item, t, isAr)}</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {itemFields.map((fieldKey) => {
                      const def = HOMEPAGE_FIELD_DEFS[fieldKey]
                      if (!def) return null
                      const label = t(def.labelKey)
                      const isFieldAr = fieldKey.endsWith('_ar')
                      const inputId = `item-${index}-${fieldKey}`

                      if (def.type === 'textarea') {
                        return (
                          <AdminFormField
                            key={fieldKey}
                            label={label}
                            htmlFor={inputId}
                            className="sm:col-span-2"
                          >
                            <textarea
                              id={inputId}
                              rows={3}
                              value={item[fieldKey] || ''}
                              onChange={(e) => updateItem(index, fieldKey, e.target.value)}
                              dir={isFieldAr ? 'rtl' : 'ltr'}
                              lang={isFieldAr ? 'ar' : 'en'}
                              className={`${adminInputClass} ${isFieldAr ? 'font-ar' : ''}`}
                            />
                          </AdminFormField>
                        )
                      }

                      if (def.type === 'image') {
                        return (
                          <AdminImageField
                            key={fieldKey}
                            label={label}
                            htmlFor={inputId}
                            className="sm:col-span-2"
                            value={item[fieldKey] || ''}
                            onChange={(image) => updateItem(index, fieldKey, image)}
                            onFileSelect={(file) => uploadItemImage(index, file)}
                            disabled={saving || uploadingItemIndex === index}
                          />
                        )
                      }

                      return (
                        <AdminFormField
                          key={fieldKey}
                          label={label}
                          htmlFor={inputId}
                          className={fieldKey === 'link' ? 'sm:col-span-2' : ''}
                        >
                          <input
                            id={inputId}
                            value={item[fieldKey] || ''}
                            onChange={(e) => updateItem(index, fieldKey, e.target.value)}
                            dir={isFieldAr ? 'rtl' : 'ltr'}
                            lang={isFieldAr ? 'ar' : 'en'}
                            className={`${adminInputClass} ${isFieldAr ? 'font-ar' : ''}`}
                          />
                        </AdminFormField>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {itemFields.length > 0 && items.length === 0 && (
          <p className="text-sm text-text/50">{t('admin.homepage.noItems')}</p>
        )}
      </div>

      <div className="border-t border-border bg-background/80 px-5 py-4">
        <Button onClick={handleSave} variant="primary" disabled={saving}>
          <Save className="h-4 w-4" />
          {t('buttons.save')}
        </Button>
      </div>
    </div>
  )
}

export default function AdminHomepage() {
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'
  const [sections, setSections] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    homepageApi
      .getAllAdmin()
      .then(({ data }) => {
        const items = data.data || data || []
        setSections(items)
        setSelectedId((current) => current ?? items[0]?.id ?? null)
      })
      .catch(() => setSections([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const selectedSection = sections.find((section) => section.id === selectedId)

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary">{t('admin.homepage.title')}</h1>
        </div>
        <Button variant="ghost" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4" />
          {t('admin.homepage.refresh')}
        </Button>
      </div>

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <Card padding={false} className="h-fit overflow-hidden lg:sticky lg:top-8">
            <div className="border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold text-primary">{t('admin.homepage.sectionsList')}</h3>
            </div>
            <div className="max-h-[420px] overflow-y-auto lg:max-h-[calc(100vh-14rem)]">
              {sections.map((section) => {
                const isSelected = section.id === selectedId
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setSelectedId(section.id)}
                    className={`flex w-full items-start justify-between gap-2 border-b border-border/60 px-4 py-3 text-start transition-colors last:border-b-0 ${
                      isSelected ? 'bg-primary/5' : 'hover:bg-background'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className={`truncate text-sm font-medium ${isSelected ? 'text-primary' : 'text-text'}`}>
                        {getSectionDisplayName(section, t, isAr)}
                      </div>
                    </div>
                    <span
                      className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${
                        section.is_active ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      title={section.is_active ? t('admin.homepage.active') : t('admin.homepage.inactive')}
                    />
                  </button>
                )
              })}
            </div>
          </Card>

          <Card padding={false} className="min-h-[420px] overflow-hidden lg:min-h-[calc(100vh-14rem)]">
            {selectedSection ? (
              <SectionEditor key={selectedSection.id} section={selectedSection} onSaved={load} />
            ) : (
              <div className="flex h-full min-h-[320px] items-center justify-center p-8 text-center text-sm text-text/50">
                {t('admin.homepage.selectSection')}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
